import {
  Button,
  Dropdown,
  Empty,
  Input,
  MenuProps,
  message,
  Result,
  Tooltip,
  Typography,
  ConfigProvider,
  notification,
  Popover,
  Badge,
} from "antd";
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import {
  TbAlertCircleFilled,
  TbBrandThreads,
  TbCheck,
  TbChevronDown,
  TbLoader2,
  TbMail,
  TbMailOff,
  TbMessageCircle,
  TbRefresh,
  TbSearch,
  TbSend,
  TbX,
  TbPlus,
  TbChevronUp,
  TbExternalLink,
  TbEye,
  TbEyeOff,
  TbQuote,
  TbEdit,
  TbPaperclip,
} from "react-icons/tb";
import { GiSewingString } from "react-icons/gi";
import { splitQuotedHtml, splitQuotedText } from "components/EmailDashboard/email-body-sanitize.util";
import {
  enrichMessage,
  formatBytes,
  formatDate,
  getContentTypeFromFile,
  getErrorMessage,
  getInitials,
  phoneFormatter,
  timeAgo,
} from "utils/functions";
import { MdOutlineKeyboardArrowDown, MdOutlineKeyboardDoubleArrowRight } from "react-icons/md";
import { ApiService } from "api/api.service";
import { IChannel, T_EmailThread, T_Message } from "dtos/communication-channel.dto";
import { useQueryClient } from "@tanstack/react-query";
import { getAvatarColor } from "./PersonList";
import EmptyInbox from "components/svgs/EmptyInbox";
import RichTextEditor from "components/RichTextEditor";
import { SectionError } from "components/Errors/SectionError";
import { useSearchParams } from "react-router-dom";
import { getClassName, getIcons } from "components/EmailDashboard/email-message";
import { GrAttachment } from "react-icons/gr";
import { GoReply } from "react-icons/go";
import AddChannelsModal from "./AddContactsModal";
import { NotFoundEmptyState } from "components/svgs/NotFoundEmptyState";
import { MessageViewSkeleton } from "./MessageSkeleton";
import EditPersonContactsModal from "./EditPersonContactsModal";
import { isAxiosError } from "axios";

const EMAIL_COLOR = "#4f46e5";
const SMS_COLOR = "#16a34a";

const COMPOSER_BUTTON = {
  activeBg: "#EEF2FF",
  activeBorder: "#C7D2FE",
  activeText: "#3730A3",
  inactiveBg: "#FFFFFF",
  inactiveBorder: "#E5E7EB",
  inactiveText: "#374151",
  hoverBg: "#F9FAFB",
};

const ConversationHeader: React.FC<{
  name: string;
  email: string;
  phone: string;
  toggleThreadPanel: () => void;
  correlationId: string;
  isPanelOpen: boolean;
  channels: IChannel[];

  onSyncChannel: (channelId: string) => void;
  openPersonList: () => void;
  isPersonListOpen: boolean;
  openAddChannelsModal: () => void;
}> = ({
  email,
  name,
  phone,
  toggleThreadPanel,
  correlationId,
  isPanelOpen,
  channels,

  onSyncChannel,
  isPersonListOpen,
  openPersonList,
  openAddChannelsModal,
}) => {
  const { bg, fg } = getAvatarColor(name);

  const actionBtnClass =
    "flex cursor-pointer bg-gray-100 items-center gap-[5px] rounded-[7px] px-[12px] py-[6px] text-[12px] font-medium text-gray-700";

  return (
    <div
      className="flex shrink-0 items-center justify-between bg-white px-[22px] py-[12px]"
      style={{ borderBottom: "1px solid #f0f0f8" }}
    >
      <div className="flex items-center gap-2">
        {!isPersonListOpen && <MdOutlineKeyboardDoubleArrowRight className="cursor-pointer" onClick={openPersonList} />}
        <div className="flex min-w-0 items-center gap-3">
          <div
            className="flex h-[42px] w-[42px] shrink-0 items-center justify-center rounded-full"
            style={{ background: bg }}
          >
            <span className="text-[14px] font-semibold" style={{ color: fg }}>
              {getInitials(name)}
            </span>
          </div>
          <div className="min-w-0">
            <div className="mb-[2px] flex items-center gap-2">
              <span className="text-[14.5px] font-semibold text-gray-900">{name}</span>
            </div>
            <div className="flex items-center gap-1">
              <Typography.Text copyable className="truncate text-[11.5px] text-gray-400 !m-0">
                {correlationId}
              </Typography.Text>
              <TbExternalLink
                onClick={() => {
                  window.open(`/dashboard/application/${correlationId}`, "_blank");
                }}
                className="mb-1 cursor-pointer"
              />
            </div>
          </div>
        </div>
      </div>

      {/* action buttons */}
      <div className="flex items-center gap-[8px]">
        {/* channels dropdown */}
        {/* <Dropdown trigger={["click"]} dropdownRender={() => channelDropdownContent} placement="bottomRight">
          <button className={actionBtnClass} style={{ border: "1px solid #e5e7eb", position: "relative" }}>
            <TbMail />
            Channels
            {selectedChannels.length > 0 && (
              <span className="flex h-[16px] min-w-[16px] items-center justify-center rounded-full bg-blue-500 px-[4px] text-[10px] font-bold text-white">
                {selectedChannels.length}
              </span>
            )}
          </button>
        </Dropdown> */}
        <button
          onClick={openAddChannelsModal}
          className={actionBtnClass}
          style={{ border: "1px solid #e5e7eb", position: "relative" }}
        >
          <TbEdit />
          Edit contacts
        </button>

        {/* thread toggle */}
        <button onClick={toggleThreadPanel} className={actionBtnClass} style={{ border: "1px solid #e5e7eb" }}>
          {isPanelOpen ? <TbX /> : <GiSewingString />}
          {isPanelOpen ? "Close" : "Thread"}
        </button>
      </div>
    </div>
  );
};

type T_BubbleProps = {
  message: T_Message;
  idx: number;
  isFocused: boolean;
  elemRef: (el: HTMLDivElement | null) => void;
  onReply: (message: T_Message) => void;
};

const MessageBubble: React.FC<T_BubbleProps> = ({ message, isFocused, elemRef, onReply }) => {
  const msg = enrichMessage(message.outgoing);
  const isHtml = !!message.html;
  const [showHtml, setShowHtml] = useState(false);
  const [showQuoted, setShowQuoted] = useState(false);

  // const { main: mainContent, quoted: quotedContent } = useMemo(() => {
  //   if (isHtml && message.html) return splitQuotedHtml(message.html);
  //   return splitQuotedText(message.text);
  // }, [isHtml, message.html, message.text]);

  // const hasQuoted = !!quotedContent;
  const htmlSplit = useMemo(() => (message.html ? splitQuotedHtml(message.html) : null), [message.html]);
  const textSplit = useMemo(() => splitQuotedText(message?.text ?? ""), [message.text]);

  const activeSplit = isHtml && showHtml ? htmlSplit! : textSplit;
  const { main: mainContent, quoted: quotedContent } = activeSplit;
  const hasQuoted = !!quotedContent;

  return (
    <div
      ref={elemRef}
      className="flex flex-col"
      style={{
        alignItems: msg.alignItems as React.CSSProperties["alignItems"],
        animation: "msgIn 0.18s ease",
        // Subtle amber flash when this message is focused from ThreadPanel
        padding: "2px 8px",
        margin: "0 -8px",
        borderRadius: 10,
        transition: "background 0.5s",
        background: isFocused ? "rgba(245, 158, 11, 0.09)" : "transparent",
      }}
    >
      <div
        className="max-w-[68%] px-[14px] py-[10px] !text-gray-800"
        style={{
          background: msg.bubbleBg,

          borderRadius: msg.bubbleRadius,
          boxShadow: msg.bubbleShadow,
          border: msg.bubbleBorder,
        }}
      >
        {isHtml && showHtml ? (
          <div className="prose prose-sm max-w-none text-[13.5px]" dangerouslySetInnerHTML={{ __html: mainContent }} />
        ) : (
          <p className="text-[13.5px] leading-[1.55] whitespace-pre-wrap">{mainContent}</p>
        )}

        {/* Quoted section — only rendered when toggled on */}
        {hasQuoted && showQuoted && (
          <div className="mt-2 pt-2" style={{ borderTop: "1px solid rgba(0,0,0,0.08)" }}>
            {isHtml && showHtml ? (
              <div
                className="prose prose-sm max-w-none text-[11.5px] opacity-60"
                dangerouslySetInnerHTML={{ __html: quotedContent! }}
              />
            ) : (
              <p className="text-[11.5px] leading-[1.55] text-gray-400 whitespace-pre-wrap">{quotedContent}</p>
            )}
          </div>
        )}
      </div>
      <div className="flex items-center gap-2">
        {isHtml && (
          <div
            onClick={() => setShowHtml((prev) => !prev)}
            role="button"
            className="flex items-center gap-1 cursor-pointer"
          >
            {showHtml ? <TbEyeOff className="text-blue-500" /> : <TbEye className="text-blue-500" />}
            <Typography.Text className="text-[10px] font-semibold text-blue-500" style={{ margin: 0 }}>
              {showHtml ? `hide html` : `show html`}
            </Typography.Text>
          </div>
        )}
        {hasQuoted && (
          <div
            onClick={() => setShowQuoted((p) => !p)}
            role="button"
            className="flex items-center gap-1 cursor-pointer"
          >
            <TbQuote className="text-gray-400" size={12} />
            <Typography.Text className="text-[10px] font-semibold text-gray-400" style={{ margin: 0 }}>
              {showQuoted ? "hide quoted" : "show quoted"}
            </Typography.Text>
          </div>
        )}
        <div onClick={() => onReply(message)} role="button" className="flex items-center gap-1 cursor-pointer">
          <GoReply className="text-purple-500" />
          <Typography.Text className="text-[10px] font-semibold text-purple-500" style={{ margin: 0 }}>
            Reply
          </Typography.Text>
        </div>

        {message.type === "email" ? (
          <Tooltip
            title={
              <div>
                <div className="flex items-center gap-1">
                  <Typography.Text className="text-[10px] text-[#b0b7c3]">from :</Typography.Text>
                  <Typography.Text className="text-[10px] text-[#b0b7c3]">{message.channel.label}</Typography.Text>
                </div>
                <div className="flex items-center gap-1">
                  <Typography.Text className="text-[10px] text-[#b0b7c3]">to :</Typography.Text>
                  <Typography.Text className="text-[10px] text-[#b0b7c3]">
                    {message.participants.join(",")}{" "}
                  </Typography.Text>
                </div>
              </div>
            }
          >
            <div className="flex items-center gap-1">
              <span style={{ color: EMAIL_COLOR, display: "flex", fontSize: 12, fontWeight: 500 }}>via email</span>
              <TbMail style={{ color: EMAIL_COLOR }} />
            </div>
          </Tooltip>
        ) : (
          <Tooltip title={""}>
            <div className="flex items-center gap-1">
              <span style={{ color: SMS_COLOR, display: "flex", fontSize: 12, fontWeight: 500 }}>via sms</span>
              <TbMessageCircle style={{ color: SMS_COLOR }} />
            </div>
          </Tooltip>
        )}
        {message.meta?.consentMessage === true && (
          <span
            className="flex items-center gap-1 rounded-full px-2 py-[2px] text-[10px] font-medium mt-[2px]"
            style={{
              background: "oklch(0.96 0.015 195)",
              color: "oklch(0.50 0.06 195)",
              width: "fit-content",
            }}
          >
            Consent request
          </span>
        )}
        <span className="mt-1 px-[2px] text-[10px] text-[#b0b7c3]">{formatDate(message.deliveredAt)}</span>
      </div>
    </div>
  );
};

const DateSeparator: React.FC<{ date: Date }> = ({ date }) => (
  <div className="flex select-none items-center gap-2.5 py-1">
    <div className="h-px flex-1 bg-gray-100" />
    <span className="whitespace-nowrap text-[10.5px] font-medium tracking-wide text-gray-400">
      {formatDateSeparator(date)}
    </span>
    <div className="h-px flex-1 bg-gray-100" />
  </div>
);

type T_ChannelSelection = { email: boolean; sms: boolean };

function ChannelPill({
  checked,
  onToggle,
  color,
  icon,
  label,
}: {
  checked: boolean;
  onToggle: () => void;
  color: string;
  icon: React.ReactNode;
  label: string;
}) {
  return (
    <div
      onClick={onToggle}
      className="flex cursor-pointer select-none items-center gap-1.5 rounded-full px-2.5 py-1 transition-all duration-150"
      style={{
        border: `1.5px solid ${checked ? color : "#e5e7eb"}`,
        background: checked ? `${color}10` : "transparent",
      }}
    >
      <span
        className="h-1.5 w-1.5 shrink-0 rounded-full transition-colors duration-150"
        style={{ background: checked ? color : "#d1d5db" }}
      />
      <span className="flex" style={{ color: checked ? color : "#6b7280" }}>
        {icon}
      </span>
      <span className="text-[12.5px] font-medium" style={{ color: checked ? color : "#6b7280" }}>
        {label}
      </span>
    </div>
  );
}
const ConsentBanner: React.FC<{
  status: string | undefined;
  isLoading: boolean;
  isPending: boolean;
  onRequest: () => void;
}> = ({ status, isLoading, isPending, onRequest }) => {
  if (isLoading) {
    return (
      <div className="border-b border-gray-100 bg-gray-50 px-4 py-1.5">
        <span className="text-[11px] text-gray-400">Checking consent…</span>
      </div>
    );
  }

  if (status === "opted_out") {
    return (
      <div
        className="flex items-center gap-2 border-b px-4 py-1.5"
        style={{ background: "oklch(0.97 0.02 25)", borderColor: "oklch(0.92 0.04 25)" }}
      >
        <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-red-500" />
        <span className="text-[11.5px] font-medium" style={{ color: "oklch(0.45 0.15 25)" }}>
          Opted out — SMS sending is blocked for this contact.
        </span>
      </div>
    );
  }

  if (status === "pending") {
    return (
      <div
        className="flex items-center gap-2 border-b px-4 py-1.5"
        style={{ background: "oklch(0.98 0.04 85)", borderColor: "oklch(0.93 0.07 85)" }}
      >
        <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-amber-500" />
        <span className="flex-1 text-[11.5px] font-medium" style={{ color: "oklch(0.50 0.10 85)" }}>
          Consent request sent — waiting for reply.
        </span>
        <Button className="!px-4 !py-4 !text-xs" size="small" loading={isPending} onClick={onRequest}>
          Re-send
        </Button>
      </div>
    );
  }

  if (status === "none" || !status) {
    return (
      <div className="flex items-center gap-2 border-b border-gray-100 bg-gray-50 px-4 py-1.5">
        <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-gray-300" />
        <span className="flex-1 text-[11.5px] text-gray-500">No consent sent yet.</span>
        <Button className="!px-4 !py-4 !text-xs" size="small" loading={isPending} onClick={onRequest}>
          Request consent
        </Button>
      </div>
    );
  }

  // opted_in
  return (
    <div
      className="flex items-center gap-2 border-b px-4 py-1.5"
      style={{ background: "oklch(0.97 0.03 145)", borderColor: "oklch(0.92 0.06 145)" }}
    >
      <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-green-500" />
      <span className="text-[11.5px] font-medium" style={{ color: "oklch(0.40 0.13 145)" }}>
        Consented
      </span>
    </div>
  );
};

function formatDateSeparator(date: Date): string {
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);
  const d = new Date(date);
  if (d.toDateString() === today.toDateString()) return "Today";
  if (d.toDateString() === yesterday.toDateString()) return "Yesterday";
  return d.toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" });
}

function isSameDay(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

const MIN_COMPOSER_HEIGHT = 300;
const MAX_COMPOSER_HEIGHT = 800;
const SMS_REPLY_TEXT_LENGTH = 150;

type T_Props = {
  person_id: string;
  deskId: string | undefined;
  focusedMessageId: string | null;
  toggleThreadPanel: () => void;
  isPanelOpen: boolean;
  desk_id?: string;
  isPersonListOpen: boolean;
  openPersonList: () => void;
  orgCode: string | null;
  selectedChannels: string[];
};

const MessageView: React.FC<T_Props> = ({
  person_id,
  focusedMessageId,
  toggleThreadPanel,
  isPanelOpen,
  deskId: desk_id,
  isPersonListOpen,
  openPersonList,
  orgCode,
  selectedChannels,
}) => {
  const [searchParams] = useSearchParams();

  const queryClient = useQueryClient();

  const [channels, setChannels] = useState({ email: true, sms: false });
  const [selectedThread, setSelectedThread] = useState<T_EmailThread | null>(null);
  const [isComposerOpen, setIsComposerOpen] = useState(false);
  const [addChannelsModal, setAddCahnnels] = useState(false);
  const [isAtBottom, setIsAtBottom] = useState(true);
  const [emailChannelSearch, setEmailChannelSearch] = useState("");
  const [smsChannelSearch, setSmsChannelSearch] = useState("");
  const [subject, setSubject] = useState("");
  const [replyText, setReplyText] = useState("");
  const [replyError, setReplyError] = useState("");
  const [selectedEmailChannel, setSelectedEmailChannel] = useState<IChannel | null>();
  const [selectedSmsChannel, setSelectedSmsChannel] = useState<IChannel | null>();
  const [selectedSenderEmail, setSelecetdSenderEmail] = useState<string | null>();
  const [selectedSenderPhone, setSelecetdSenderPhone] = useState<string | null>();
  const [subjectError, setSubjectError] = useState(false);
  const [attachedFiles, setAttachedFiles] = useState<File[]>([]);
  const [attachmentPopoverOpen, setAttachmentPopoverOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const composerWrapperRef = useRef<HTMLDivElement>(null);
  // single ref object — no re-renders during drag
  const dragRef = useRef({ isDragging: false, startY: 0, startHeight: 0 });

  const [composerHeight, setComposerHeight] = useState(300);

  const [focused, setFocused] = useState(false);
  const getThreadOFPerson = ApiService.communicationChannel.useGetThreadsByPerson({ person_id, page: 1, limt: 20 });
  const getThreadData = getThreadOFPerson.data?.result.data;
  const syncChannels = ApiService.communicationChannel.useSyncChannel();
  const toggle = (channel: keyof T_ChannelSelection) => setChannels({ ...channels, [channel]: !channels[channel] });

  const [threadSearch, setThreadSearch] = useState("");

  const filteredThreads = useMemo(
    () =>
      (getThreadData ?? []).filter((t: T_EmailThread) =>
        (t.subject ?? "(No Subject)").toLowerCase().includes(threadSearch.toLowerCase()),
      ),
    [getThreadData, threadSearch],
  );

  const scrollRef = useRef<HTMLDivElement>(null);
  const messageRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  const prevScrollHeightRef = useRef(0); // stored before fetchNextPage fires
  const initialScrolledRef = useRef(false); // one-shot scroll to bottom on load
  const pendingScrollBottomRef = useRef(false); // scroll to bottom after send+refetch
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const picked = Array.from(e.target.files ?? []);
    if (picked.length) setAttachedFiles((prev) => [...prev, ...picked]);
    e.target.value = ""; // reset so the same file can be re-picked
  };

  // const scrollCallbackRef = (node: HTMLDivElement | null) => {
  //   scrollRef.current = node;
  //   setScrollReady(!!node);
  // };

  const handleRemoveFile = (index: number) => {
    setAttachedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  // reset popover state when last file is removed so it doesn't auto-reopen
  useEffect(() => {
    if (attachedFiles.length === 0) setAttachmentPopoverOpen(false);
  }, [attachedFiles.length]);

  // Stable ref so the scroll listener never becomes stale without re-attaching
  const scrollStateRef = useRef<{
    hasNextPage: boolean;
    isFetchingNextPage: boolean;
    fetchNextPage: () => void;
  }>({
    hasNextPage: false,
    isFetchingNextPage: false,
    fetchNextPage: () => {},
  });
  // ─ Queries

  const getPersonDetails = ApiService.communicationChannel.useGetPerson({ person_id });

  const personDetails = getPersonDetails.data?.result;
  const sendReplyMutation = ApiService.communicationChannel.useSendMessage();
  const broadcastMessage = ApiService.communicationChannel.useBroadcastMessage();
  const requestConsent = ApiService.communicationChannel.useRequestConsent();
  const smsConsentQuery = ApiService.communicationChannel.useGetSmsConsent({
    personId: person_id,
    channelId: selectedSmsChannel?._id as string,
    enabled: channels.sms && !!selectedSmsChannel && !!selectedSenderPhone,
  });
  const smsConsent = smsConsentQuery.data?.result;
  const getDeskDetails = ApiService.communicationChannel.useGetDeskByID({
    desk_id: desk_id ?? null,
    refetchInterval: (query) => {
      // const desk = query.state.data?.result;
      // const person = getPersonDetails.data?.result;
      // if (!desk || !person) return 2000; // still loading, keep polling
      // const synced = desk.lastSyncedAt && new Date(desk.lastSyncedAt) >= new Date(person.createdAt);
      // return synced ? false : 2000; // stop when condition is met
      return false;
    },
  });

  const deskDetails = getDeskDetails.data?.result;

  const handleSyncChannel = (channelId: string) => {
    syncChannels.mutate(
      { channel_id: channelId, desk_id: desk_id as string },
      { onSuccess: () => message.success("Email synced in queue") },
    );
  };

  const availableEmailChannels = useMemo(
    () => deskDetails?.channels.filter((ch) => ch.type === "email"),
    [deskDetails],
  );

  const availableSmsChannels = useMemo(() => deskDetails?.channels.filter((ch) => ch.type === "sms"), [deskDetails]);
  const personEmails = useMemo(
    () =>
      personDetails?.emails.map((ch) => ({
        key: ch,
        label: (
          <div className="flex flex-col gap-[1px] py-[2px]">
            <span className="text-[12px] font-semibold">{ch}</span>
          </div>
        ),
      })) ?? [],
    [personDetails?.emails],
  );

  // const personNumbers = useMemo(
  //   () =>
  //     personDetails?.numbers.map((num) => ({
  //       key: num,
  //       label: (
  //         <div className="flex flex-col gap-[1px] py-[2px]">
  //           <span className="text-[12px] font-semibold">{num}</span>
  //         </div>
  //       ),
  //     })) ?? [],
  //   [personDetails?.numbers],
  // );
  const personNumbers = personDetails?.numbers.map((num) => ({
    key: num,
    label: (
      <div className="flex flex-col gap-[1px] py-[2px]">
        <span className="text-[12px] font-semibold">{num}</span>
      </div>
    ),
  }));

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isFetching,
    isLoading,
    refetch: inboxRefetch,
    isError,
    error,
  } = ApiService.communicationChannel.useGetInboxOfPerson({
    person_id,
    enabled: Boolean(person_id), // only fires once sync condition is cleared
    selectedChannels,
  });
  // API returns newest-first; flatMap across pages then reverse → oldest at top, newest at bottom
  const messages = useMemo(() => (data?.pages ?? []).flatMap((p) => p.result.data).reverse(), [data?.pages]);

  // Keep scroll state ref current every render (safe because listeners read via ref)
  scrollStateRef.current = {
    hasNextPage: !!hasNextPage,
    isFetchingNextPage,
    fetchNextPage: () => {
      fetchNextPage();
    }, // wraps and discards the Promise return
  };

  // ─ Reset when person switches ─
  useEffect(() => {
    initialScrolledRef.current = false;
    messageRefs.current.clear();
    setSelectedThread(null);
  }, [person_id]);

  // ─ Scroll to bottom on first load
  useEffect(() => {
    if (isLoading || messages.length === 0 || initialScrolledRef.current) return;
    const el = scrollRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
    initialScrolledRef.current = true;
  }, [isLoading, messages.length]);

  // ─ Scroll to bottom after send (waits for invalidation refetch to finish)
  useEffect(() => {
    if (!pendingScrollBottomRef.current || isFetching) return;
    const el = scrollRef.current;
    if (!el) return;
    el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
    pendingScrollBottomRef.current = false;
  }, [isFetching]);

  // ─ Restore scroll position after older messages are prepended ─
  // useLayoutEffect fires synchronously after DOM paint — prevents visible jump
  useLayoutEffect(() => {
    if (!prevScrollHeightRef.current) return;
    const el = scrollRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight - prevScrollHeightRef.current;
    prevScrollHeightRef.current = 0;
  }, [messages.length]);

  const scrollListenerRef = useRef<((this: HTMLDivElement, ev: Event) => any) | null>(null);
  const scrollCallbackRef = useCallback((node: HTMLDivElement | null) => {
    // 1. Remove old listener if it exists
    if (scrollListenerRef.current && scrollRef.current) {
      scrollRef.current.removeEventListener("scroll", scrollListenerRef.current);
      scrollListenerRef.current = null;
    }

    // 2. Update the ref to the new node
    // @ts-expect-error -- React ref is intentionally being assigned here
    scrollRef.current = node;

    if (!node) return;

    const onScroll = () => {
      const { hasNextPage, isFetchingNextPage, fetchNextPage } = scrollStateRef.current;
      const distanceFromBottom = node.scrollHeight - node.scrollTop - node.clientHeight;
      setIsAtBottom(distanceFromBottom < 80);
      if (node.scrollTop < 80 && hasNextPage && !isFetchingNextPage) {
        prevScrollHeightRef.current = node.scrollHeight;
        fetchNextPage();
      }
    };

    node.addEventListener("scroll", onScroll, { passive: true });
    scrollListenerRef.current = onScroll;
  }, []);

  useEffect(() => {
    if (!focusedMessageId) return;
    const el = messageRefs.current.get(focusedMessageId);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [focusedMessageId]);
  useEffect(() => {
    if (!availableEmailChannels?.length) return;

    const defaultChannel = availableEmailChannels.find((ch) => ch._id === deskDetails?.defaultEmailChannelId);

    setSelectedEmailChannel(defaultChannel ?? availableEmailChannels[0]);
  }, [availableEmailChannels, desk_id]);

  useEffect(() => {
    if (!availableSmsChannels?.length) return;

    const defaultChannel = availableSmsChannels.find((ch) => ch._id === deskDetails?.defaultSmsChannelId);
    if ((availableEmailChannels ?? [])?.length === 0 && (availableSmsChannels ?? [])?.length !== 0) {
      setChannels({ email: false, sms: true });
    }

    setSelectedSmsChannel(defaultChannel ?? availableSmsChannels[0]);
  }, [availableSmsChannels, desk_id]);

  useEffect(() => {
    setSelecetdSenderPhone(personDetails?.numbers?.[0] ?? undefined);
  }, [personDetails]);

  useEffect(() => {
    setSelecetdSenderEmail(personDetails?.emails?.[0] ?? undefined);
  }, [personDetails]);
  useEffect(() => {
    if (selectedThread) {
      setSubject(`Re: ${selectedThread.subject ?? ""}`);
    } else {
      setSubject("");
    }
  }, [selectedThread]);

  useEffect(() => {
    setSelectedThread(getThreadData?.at(0) ?? null);
  }, [getThreadData]);
  const handleDragStart = (e: React.MouseEvent) => {
    e.preventDefault();
    dragRef.current = {
      isDragging: true,
      startY: e.clientY,
      startHeight: composerWrapperRef.current?.offsetHeight ?? composerHeight,
    };

    if (composerWrapperRef.current) {
      composerWrapperRef.current.style.transition = "none";
    }
  };

  useEffect(() => {
    const onMove = (e: MouseEvent) => {
      if (!dragRef.current.isDragging) return;
      const delta = dragRef.current.startY - e.clientY; // drag up = positive = taller
      const next = Math.min(Math.max(dragRef.current.startHeight + delta, MIN_COMPOSER_HEIGHT), MAX_COMPOSER_HEIGHT);
      // direct DOM write — zero React re-renders per mousemove
      if (composerWrapperRef.current) {
        composerWrapperRef.current.style.height = `${next}px`;
      }
    };

    const onUp = () => {
      if (!dragRef.current.isDragging) return;
      dragRef.current.isDragging = false;

      const final = composerWrapperRef.current?.offsetHeight ?? composerHeight;
      setComposerHeight(final);

      if (composerWrapperRef.current) {
        composerWrapperRef.current.style.transition = "height 0.3s cubic-bezier(0.4, 0, 0.2, 1)";
      }
    };

    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
  }, []);
  const emailMenuItems = useMemo<MenuProps["items"]>(
    () =>
      availableEmailChannels?.map((ch) => ({
        key: ch._id.toString(),
        label: (
          <div className="flex flex-col gap-[1px] py-[2px]">
            <span className="text-[12px] font-semibold">{ch.label}</span>

            <div className="flex items-center justify-between">
              <span
                style={{
                  fontSize: 10,
                  fontWeight: 600,
                  padding: "1px 6px",
                  borderRadius: 4,
                  textTransform: "capitalize",
                  background: "oklch(0.92 0.006 240)",
                  color: "oklch(0.45 0.015 255)",
                }}
              >
                {ch.provider}
              </span>
              <span className="text-[10.5px] text-gray-400">
                {ch.lastSyncedAt ? `Synced ${timeAgo(ch.lastSyncedAt)} ago` : "Never synced"}
              </span>
            </div>
          </div>
        ),
      })) ?? [],
    [availableEmailChannels],
  );

  const smsMenuItems = useMemo<MenuProps["items"]>(
    () =>
      availableSmsChannels?.map((ch) => ({
        key: ch._id.toString(),
        label: (
          <div className="flex flex-col gap-[1px] py-[2px]">
            <span className="text-[12px] font-semibold">{ch.label}</span>
          </div>
        ),
      })) ?? [],
    [availableSmsChannels],
  );

  // ─ Send ─
  const onSuccess = () => {
    message.success("Reply sent");
    setReplyText("");
    setSubject("");
    pendingScrollBottomRef.current = true;
    queryClient.invalidateQueries({ queryKey: ["persons", person_id, "messages"] });
  };

  const handleSend = () => {
    if (channels.sms && smsConsent?.status === "opted_out") {
      message.error("Cannot send SMS — this person has opted out of receiving messages.");
      return;
    }
    if (!selectedThread && !subject.trim() && channels.email) return void setSubjectError(true);
    if (!replyText.trim()) return void setReplyError("Reply body is required to send an message");

    const bothSelected = channels.email && channels.sms;
    if (!bothSelected) {
      if (channels.sms && replyText.length > SMS_REPLY_TEXT_LENGTH) {
        // return notification.info({
        //   message: `SMS messages are limited to 1000 characters. Please shorten your message (currently ${replyText.length} characters).`,
        // });
        setReplyError(
          `SMS messages are limited to ${SMS_REPLY_TEXT_LENGTH} characters. Please shorten your message (currently ${replyText.length} characters).`,
        );
        return;
      }
    }

    if (bothSelected) {
      if (!selectedEmailChannel) return void message.error("No email channel available to send from");
      if (!selectedSenderEmail) return void message.error("No recipient email found for this person");
      if (!selectedSmsChannel) return void message.error("No SMS channel available to send from");
      if (!selectedSenderPhone) return void message.error("No recipient phone number found for this person");

      broadcastMessage.mutate(
        {
          person_id,
          body: {
            channels: [
              { channelId: selectedEmailChannel._id as string, contact: selectedSenderEmail },
              { channelId: selectedSmsChannel._id as string, contact: selectedSenderPhone },
            ],
            html: replyText,
            subject: subject,
            threadId: selectedThread?._id,
          },
        },
        {
          onSuccess,
          onError: (err: unknown) => {
            if (isAxiosError(err) && err.response?.status === 403) {
              message.error("SMS blocked — this person has opted out of receiving messages.");
            } else {
              message.error(getErrorMessage(err));
            }
          },
        },
      );
    } else if (channels.email) {
      if (!selectedEmailChannel) return void message.error("No email channel available to send from");
      if (!selectedSenderEmail) return void message.error("No recipient email found for this person");

      sendReplyMutation.mutate(
        {
          person_id,
          body: {
            channelId: selectedEmailChannel._id as string,
            html: replyText,
            contact: selectedSenderEmail,
            threadId: selectedThread?._id,
            subject: subject,
          },
        },
        { onSuccess },
      );
    } else if (channels.sms) {
      if (!selectedSmsChannel) return void message.error("No SMS channel available to send from");
      if (!selectedSenderPhone) return void message.error("No recipient phone number found for this person");

      sendReplyMutation.mutate(
        {
          person_id,
          body: {
            channelId: selectedSmsChannel._id as string,
            html: replyText,
            contact: selectedSenderPhone,
          },
        },
        {
          onSuccess,
          onError: (err: unknown) => {
            if (isAxiosError(err) && err.response?.status === 403) {
              message.error("SMS blocked — this person has opted out of receiving messages.");
            } else {
              message.error(getErrorMessage(err));
            }
          },
        },
      );
    }
  };

  if (getDeskDetails.isPending || isLoading) {
    return (
      <div className="flex-1 flex items-center justify-center h-full">
        <MessageViewSkeleton />
      </div>
    );
  }
  if ((deskDetails?.channels ?? []).length === 0) {
    return (
      <div className="flex-1 flex h-full items-center justify-center">
        <NotFoundEmptyState
          title="No Channels"
          subtitle="No channels have been added to this desk. Configure a channel to get started."
          actionBtn={
            <Button
              icon={<TbPlus />}
              href={`/dashboard/organisation/${orgCode}/channel?desk_id=${desk_id}&orgCode=${orgCode}&src=org`}
            >
              Add Channel
            </Button>
          }
        />
      </div>
    );
  }

  if (!person_id) {
    return (
      <div style={{ flex: "1" }} className="flex items-center justify-center h-[95vh] text-gray-500">
        <EmptyInbox title="No Chat Selected" subtitle="Select a chat to view all the messages" />
      </div>
    );
  }

  // if (needsSync) {
  //   const emailChannels = deskDetails?.channels.filter((ch) => ch.type === "email") ?? [];
  //   return <SyncingScreen emailChannels={emailChannels} desk_id={desk_id!} />;
  // }
  if (isError) {
    return (
      <SectionError className="flex-1">
        <div className="flex items-center justify-center p-5 max-w-[700px] mx-auto">
          <Result
            icon={<TbAlertCircleFilled size={72} className={`mx-auto text-red-500`} />}
            status={"error"}
            // title={query.error.response?.statusText}
            subTitle={getErrorMessage(error)}
            //   extra={<Button href="/dashboard">Go Dashboard</Button>}
          />
        </div>
      </SectionError>
    );
  }
  if (getDeskDetails.isError || getPersonDetails.isError) {
    return (
      <SectionError className="flex-1">
        <div className="flex items-center justify-center p-5 max-w-[700px] mx-auto">
          <Result
            icon={<TbAlertCircleFilled size={72} className={`mx-auto text-red-500`} />}
            status={"error"}
            // title={query.error.response?.statusText}
            subTitle={getErrorMessage(getDeskDetails.error || getPersonDetails.error)}
            //   extra={<Button href="/dashboard">Go Dashboard</Button>}
          />
        </div>
      </SectionError>
    );
  }

  // Build date-separated message list
  const messageItems: Array<{ type: "separator"; date: Date } | { type: "message"; msg: T_Message }> = [];
  messages.forEach((msg, i) => {
    const msgDate = new Date(msg.deliveredAt);
    const prevMsg = messages[i - 1];
    if (!prevMsg || !isSameDay(new Date(prevMsg.deliveredAt), msgDate)) {
      messageItems.push({ type: "separator", date: msgDate });
    }
    messageItems.push({ type: "message", msg });
  });

  return (
    <div style={{ flex: 1, background: "#fff", display: "flex", flexDirection: "column", minWidth: 0 }}>
      {/* <PageTitle
        title={(personDetails?.meta?.name as any) ?? "Communication Chat"}
        pageTitle={[{ title: "Communication" }]}
      /> */}

      <ConversationHeader
        name={(personDetails?.meta?.name ?? "-") as string}
        email={personDetails?.emails ? personDetails?.emails.join(",") : "-"}
        phone={personDetails?.numbers ? personDetails?.numbers.join(",") : "-"}
        toggleThreadPanel={toggleThreadPanel}
        correlationId={personDetails?.correlation_id as string}
        isPanelOpen={isPanelOpen}
        channels={availableEmailChannels ?? []}
        onSyncChannel={handleSyncChannel}
        isPersonListOpen={isPersonListOpen}
        openPersonList={openPersonList}
        openAddChannelsModal={() => setAddCahnnels(true)}
      />
      <div style={{ flex: 1, display: "flex", flexDirection: "column", minHeight: 0 }}>
        {
          <div
            ref={scrollCallbackRef}
            style={{
              flex: 1,
              overflowY: "auto",
              padding: "24px 32px",
              display: "flex",
              flexDirection: "column",
              gap: 14,
              background: "#fafafa",
            }}
          >
            {!isAtBottom && !isComposerOpen && (
              <button
                onClick={() => scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" })}
                style={{
                  position: "absolute",
                  bottom: 64,
                  alignSelf: "center",
                  zIndex: 10,
                  width: 36,
                  height: 36,
                  borderRadius: "50%",
                  background: "#6366f1",
                  border: "none",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  boxShadow: "0 2px 8px rgba(99,102,241,0.35)",
                  transition: "opacity 0.2s, transform 0.2s",
                }}
              >
                <TbChevronDown size={20} color="#fff" />
              </button>
            )}
            {/* Spinner while loading an older page (scroll-to-top triggered) */}
            {isFetchingNextPage && (
              <div className="flex justify-center py-3">
                <TbLoader2 size={20} className="stroke-purple-600 animate-spin" />
              </div>
            )}

            {/* End-of-history marker */}
            {!isLoading && !hasNextPage && messages.length > 0 && (
              <div style={{ textAlign: "center", fontSize: 11, color: "#d1d5db", paddingBottom: 4 }}>
                Beginning of conversation
              </div>
            )}

            {/* Initial loading */}
            {isLoading && (
              <div className="flex justify-center py-8">
                <TbLoader2 size={20} className="stroke-purple-600 animate-spin" />
              </div>
            )}

            {messages.length === 0 ? (
              <div className="flex items-center justify-center h-[30vh]">
                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No message found">
                  <Button onClick={() => inboxRefetch()} icon={<TbRefresh />}>
                    Refresh
                  </Button>
                </Empty>
              </div>
            ) : (
              messageItems.map((item, i) =>
                item.type === "separator" ? (
                  <DateSeparator key={`sep-${i}`} date={item.date} />
                ) : (
                  <MessageBubble
                    key={item.msg._id}
                    message={item.msg}
                    idx={i}
                    isFocused={focusedMessageId === item.msg._id}
                    elemRef={(el) => {
                      if (el) messageRefs.current.set(item.msg._id, el);
                      else messageRefs.current.delete(item.msg._id);
                    }}
                    onReply={(msg) => {
                      setSelectedThread(msg.thread);
                      setIsComposerOpen(true);
                    }}
                  />
                ),
              )
            )}

            {/* Invisible anchor; scrollIntoView target after send */}
            <div style={{ height: 0 }} aria-hidden />
          </div>
        }

        {/*  Composer ─ */}

        {/* Collapsed toggle bar — always visible */}
        <div className="shrink-0 border-t border-gray-100 bg-white">
          {/* Composer toolbar — channel pills + thread selector, always visible */}
          <div
            className={`flex items-center justify-between gap-2 px-4 py-2 ${
              isComposerOpen ? "border-b border-gray-100" : ""
            }`}
          >
            {/* Left: channel pills */}
            <div className="flex items-center gap-1.5">
              <span className="mr-0.5 text-[11px] font-medium text-gray-400">Send via</span>
              <ChannelPill
                checked={channels.email}
                onToggle={() => toggle("email")}
                color={EMAIL_COLOR}
                icon={<TbMail size={13} />}
                label="Email"
              />
              {(availableSmsChannels ?? []).length > 0 && (
                <ChannelPill
                  checked={channels.sms}
                  onToggle={() => toggle("sms")}
                  color={SMS_COLOR}
                  icon={<TbMessageCircle size={13} />}
                  label="SMS"
                />
              )}
            </div>

            {/* Right: thread selector + open/close toggle */}
            <div className="flex items-center gap-1.5">
              {channels.email && (
                <div className="flex items-center gap-1">
                  <Dropdown
                    trigger={["click"]}
                    onOpenChange={(open) => {
                      if (!open) setThreadSearch("");
                    }}
                    dropdownRender={() => (
                      <div
                        className="overflow-hidden rounded-lg border"
                        style={{
                          width: 300,
                          borderColor: "var(--ant-color-border)",
                          background: "var(--ant-color-bg-elevated)",
                          boxShadow: "0 6px 20px rgba(0,0,0,0.10)",
                        }}
                      >
                        <div
                          className="border-b px-2 py-1.5"
                          style={{ borderColor: "var(--ant-color-border-secondary)" }}
                        >
                          <Input
                            autoFocus
                            size="small"
                            placeholder="Search threads…"
                            prefix={<TbSearch size={13} className="text-gray-400" />}
                            value={threadSearch}
                            onChange={(e) => setThreadSearch(e.target.value)}
                            allowClear
                          />
                        </div>
                        <div className="max-h-60 overflow-y-auto">
                          {filteredThreads.length === 0 ? (
                            <div className="flex flex-col items-center justify-center gap-1 py-5">
                              <TbMailOff size={18} className="text-gray-300" />
                              <span className="text-[11.5px] text-gray-400">No threads</span>
                            </div>
                          ) : (
                            filteredThreads.map((thread: T_EmailThread) => {
                              const isSelected = selectedThread?.threadKey === thread.threadKey;
                              return (
                                <div
                                  key={thread.threadKey}
                                  onClick={() => setSelectedThread(thread)}
                                  className="flex cursor-pointer items-start gap-2 px-3 py-2"
                                  style={{
                                    background: isSelected ? "var(--ant-color-primary-bg)" : undefined,
                                    borderLeft: isSelected
                                      ? "2px solid var(--ant-color-primary)"
                                      : "2px solid transparent",
                                  }}
                                >
                                  <TbMail
                                    size={12}
                                    className="mt-0.5 shrink-0"
                                    style={{ color: isSelected ? "var(--ant-color-primary)" : "#a1a1aa" }}
                                  />
                                  <span
                                    className="flex-1 overflow-hidden text-ellipsis whitespace-nowrap text-[12px] leading-[1.4]"
                                    style={{
                                      color: isSelected ? "var(--ant-color-primary)" : "var(--ant-color-text)",
                                    }}
                                  >
                                    {thread.subject ?? "(No Subject)"}
                                  </span>
                                  {isSelected && (
                                    <TbCheck
                                      size={12}
                                      className="mt-0.5 shrink-0"
                                      style={{ color: "var(--ant-color-primary)" }}
                                    />
                                  )}
                                </div>
                              );
                            })
                          )}
                        </div>
                      </div>
                    )}
                  >
                    <Button
                      size="small"
                      icon={<TbMail size={13} />}
                      className="max-w-[200px] overflow-hidden px-3.5 py-4"
                    >
                      <span className="overflow-hidden text-ellipsis whitespace-nowrap">
                        {selectedThread ? (selectedThread.subject ?? "(No Subject)") : "Thread"}
                      </span>
                      <TbChevronDown size={12} className="shrink-0 text-gray-400" />
                    </Button>
                  </Dropdown>
                  {selectedThread && (
                    <Tooltip title="Clear thread">
                      <Button
                        type="text"
                        size="small"
                        onClick={() => setSelectedThread(null)}
                        icon={<TbX size={13} />}
                      />
                    </Tooltip>
                  )}
                </div>
              )}

              <button
                onClick={() => setIsComposerOpen((p) => !p)}
                className="flex items-center gap-1.5 rounded-lg px-3.5 py-2 text-[12px] font-semibold transition-all duration-200 cursor-pointer select-none outline-none"
                style={{
                  // Inactive: Vibrant Indigo | Active: Light Soft Tint
                  background: isComposerOpen ? "#EEF2FF" : "#4F46E5",
                  color: isComposerOpen ? "#4338CA" : "#FFFFFF",
                  border: `1px solid ${isComposerOpen ? "#C7D2FE" : "#4338CA"}`,
                }}
                onMouseEnter={(e) => {
                  if (!isComposerOpen) e.currentTarget.style.background = "#4338CA";
                }}
                onMouseLeave={(e) => {
                  if (!isComposerOpen) e.currentTarget.style.background = "#4F46E5";
                }}
              >
                <TbChevronUp
                  size={14}
                  className="transition-transform duration-200 ease-in-out"
                  style={{
                    transform: isComposerOpen ? "rotate(180deg)" : "rotate(0deg)",
                  }}
                />
                <span>{isComposerOpen ? "Close Composer" : "Reply"}</span>
              </button>
            </div>
          </div>

          {/* Expandable composer body */}
          <div
            ref={composerWrapperRef}
            className="flex flex-col overflow-hidden"
            style={{
              height: isComposerOpen ? composerHeight : 0,
              transition: "height 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
            }}
          >
            {/* Drag handle */}
            <div
              onMouseDown={handleDragStart}
              className="flex shrink-0 cursor-ns-resize items-center justify-center gap-1.5 border-b border-gray-100 bg-gray-50"
              style={{ height: 16 }}
            >
              <div className="h-0.5 w-8 rounded-full bg-gray-200" />
              <span className="text-[10px] font-medium tracking-wide text-gray-400">drag to resize</span>
              <div className="h-0.5 w-8 rounded-full bg-gray-200" />
            </div>

            {/* SMS consent banner — shown only when SMS is active */}
            {channels.sms && selectedSmsChannel && selectedSenderPhone && (
              <ConsentBanner
                status={smsConsent?.status}
                isLoading={smsConsentQuery.isLoading}
                isPending={requestConsent.isPending}
                onRequest={() => {
                  requestConsent.mutate(
                    {
                      personId: person_id,
                      channelId: selectedSmsChannel._id as string,
                      number: selectedSenderPhone,
                    },
                    {
                      onSuccess: () => message.success("Consent message sent"),
                      onError: () => message.error("Failed to send consent message"),
                    },
                  );
                }}
              />
            )}

            <div className="flex min-h-0 flex-1 flex-col bg-white px-4 pb-3 pt-2.5 space-y-3">
              {/* Routing row */}
              <div className="mb-2 flex flex-wrap gap-3">
                {channels.email && selectedEmailChannel && (
                  <div className="flex items-center gap-1.5">
                    <span className="text-[11px] font-medium text-gray-400">From</span>
                    <Dropdown
                      trigger={["click"]}
                      onOpenChange={(open) => {
                        if (!open) setEmailChannelSearch("");
                      }}
                      dropdownRender={() => (
                        <div
                          className="overflow-hidden rounded-lg border"
                          style={{
                            width: 260,
                            borderColor: "var(--ant-color-border)",
                            background: "var(--ant-color-bg-elevated)",
                            boxShadow: "0 6px 20px rgba(0,0,0,0.10)",
                          }}
                        >
                          <div
                            className="border-b px-2 py-1.5"
                            style={{ borderColor: "var(--ant-color-border-secondary)" }}
                          >
                            <Input
                              autoFocus
                              size="small"
                              placeholder="Search channels…"
                              prefix={<TbSearch size={13} className="text-gray-400" />}
                              value={emailChannelSearch}
                              onChange={(e) => setEmailChannelSearch(e.target.value)}
                              allowClear
                            />
                          </div>
                          <div className="max-h-[220px] overflow-y-auto">
                            {(availableEmailChannels ?? [])
                              .filter((ch) => ch.label.toLowerCase().includes(emailChannelSearch.toLowerCase()))
                              .map((ch) => {
                                const isSelected = selectedEmailChannel?._id === ch._id;
                                return (
                                  <div
                                    key={ch._id.toString()}
                                    onClick={() => setSelectedEmailChannel(ch)}
                                    className="flex cursor-pointer flex-col gap-0.5 px-3 py-2"
                                    style={{
                                      background: isSelected ? "var(--ant-color-primary-bg)" : undefined,
                                      borderLeft: isSelected
                                        ? "2px solid var(--ant-color-primary)"
                                        : "2px solid transparent",
                                    }}
                                  >
                                    {ch.alias ? (
                                      <>
                                        <span className="text-[12px] font-semibold">{ch.alias}</span>
                                        <span className="text-[10px] text-gray-400">{ch.label}</span>
                                      </>
                                    ) : (
                                      <span className="text-[12px] font-semibold">{ch.label}</span>
                                    )}
                                    <div className="flex items-center justify-between">
                                      <span
                                        className="rounded px-1.5 py-px text-[10px] font-semibold capitalize"
                                        style={{
                                          background: "oklch(0.92 0.006 240)",
                                          color: "oklch(0.45 0.015 255)",
                                        }}
                                      >
                                        {ch.provider}
                                      </span>
                                      <span className="text-[10.5px] text-gray-400">
                                        {ch.lastSyncedAt ? `Synced ${timeAgo(ch.lastSyncedAt)} ago` : "Never synced"}
                                      </span>
                                    </div>
                                  </div>
                                );
                              })}
                            {(availableEmailChannels ?? []).filter((ch) =>
                              ch.label.toLowerCase().includes(emailChannelSearch.toLowerCase()),
                            ).length === 0 && (
                              <div className="flex flex-col items-center justify-center gap-1 py-5">
                                <TbMailOff size={18} className="text-gray-300" />
                                <span className="text-[11.5px] text-gray-400">No channels</span>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    >
                      <div
                        className="flex cursor-pointer items-center gap-1.5 rounded-full px-2.5 py-0.5"
                        style={{
                          border: `1px solid ${EMAIL_COLOR}33`,
                          background: `${EMAIL_COLOR}08`,
                        }}
                      >
                        <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-green-500" />
                        <span className="text-[11.5px] font-medium" style={{ color: EMAIL_COLOR }}>
                          {selectedEmailChannel.alias ?? selectedEmailChannel.label}
                        </span>
                        <MdOutlineKeyboardArrowDown style={{ color: EMAIL_COLOR }} />
                      </div>
                    </Dropdown>
                    <span className="text-[11px] font-medium text-gray-400">to</span>
                    <Dropdown
                      trigger={["click"]}
                      menu={{
                        items: personEmails,
                        selectedKeys: selectedSenderEmail ? [selectedSenderEmail] : undefined,
                        onClick: ({ key }) => setSelecetdSenderEmail(key),
                      }}
                    >
                      <div
                        className="flex cursor-pointer items-center gap-1.5 rounded-full px-2.5 py-0.5"
                        style={{
                          border: `1px solid ${EMAIL_COLOR}33`,
                          background: `${EMAIL_COLOR}08`,
                        }}
                      >
                        <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-green-500" />
                        <span className="text-[11.5px] font-medium" style={{ color: EMAIL_COLOR }}>
                          {selectedSenderEmail ?? "-"}
                        </span>
                        <MdOutlineKeyboardArrowDown style={{ color: EMAIL_COLOR }} />
                      </div>
                    </Dropdown>
                  </div>
                )}

                {channels.sms && selectedSmsChannel && (
                  <div
                    className="flex items-center gap-1.5"
                    style={{
                      paddingLeft: channels.email ? 12 : 0,
                      borderLeft: channels.email ? "1px solid #f0f0f8" : "none",
                    }}
                  >
                    <span className="text-[11px] font-medium text-gray-400">From</span>
                    <Dropdown
                      trigger={["click"]}
                      onOpenChange={(open) => {
                        if (!open) setSmsChannelSearch("");
                      }}
                      dropdownRender={() => (
                        <div
                          className="overflow-hidden rounded-lg border"
                          style={{
                            width: 240,
                            borderColor: "var(--ant-color-border)",
                            background: "var(--ant-color-bg-elevated)",
                            boxShadow: "0 6px 20px rgba(0,0,0,0.10)",
                          }}
                        >
                          <div
                            className="border-b px-2 py-1.5"
                            style={{ borderColor: "var(--ant-color-border-secondary)" }}
                          >
                            <Input
                              autoFocus
                              size="small"
                              placeholder="Search channels…"
                              prefix={<TbSearch size={13} className="text-gray-400" />}
                              value={smsChannelSearch}
                              onChange={(e) => setSmsChannelSearch(e.target.value)}
                              allowClear
                            />
                          </div>
                          <div className="max-h-[220px] overflow-y-auto">
                            {(availableSmsChannels ?? [])
                              .filter(
                                (ch) =>
                                  ch.label.toLowerCase().includes(smsChannelSearch.toLowerCase()) ||
                                  ch?.alias?.toLowerCase().includes(smsChannelSearch.toLowerCase()),
                              )
                              .map((ch) => {
                                const isSelected = selectedSmsChannel?._id === ch._id;
                                return (
                                  <div
                                    key={ch._id.toString()}
                                    onClick={() => setSelectedSmsChannel(ch)}
                                    className="flex cursor-pointer flex-col gap-0.5 px-3 py-2"
                                    style={{
                                      background: isSelected ? "var(--ant-color-primary-bg)" : undefined,
                                      borderLeft: isSelected
                                        ? "2px solid var(--ant-color-primary)"
                                        : "2px solid transparent",
                                    }}
                                  >
                                    {ch.alias ? (
                                      <>
                                        <span className="text-[12px] font-semibold">{ch.alias}</span>
                                        <span className="text-[10px] text-gray-400">{phoneFormatter(ch.label)}</span>
                                      </>
                                    ) : (
                                      <span className="text-[12px] font-semibold">{phoneFormatter(ch.label)}</span>
                                    )}
                                  </div>
                                );
                              })}
                            {(availableSmsChannels ?? []).filter(
                              (ch) =>
                                ch.label.toLowerCase().includes(smsChannelSearch.toLowerCase()) ||
                                ch?.alias?.toLowerCase().includes(smsChannelSearch.toLowerCase()),
                            ).length === 0 && (
                              <div className="flex flex-col items-center justify-center gap-1 py-5">
                                <TbMessageCircle size={18} className="text-gray-300" />
                                <span className="text-[11.5px] text-gray-400">No channels</span>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    >
                      <div
                        className="flex cursor-pointer items-center gap-1.5 rounded-full px-2.5 py-0.5"
                        style={{
                          border: `1px solid ${SMS_COLOR}33`,
                          background: `${SMS_COLOR}08`,
                        }}
                      >
                        <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-green-500" />
                        <span className="text-[11.5px] font-medium" style={{ color: SMS_COLOR }}>
                          {selectedSmsChannel?.alias ?? phoneFormatter(selectedSmsChannel.label)}
                        </span>
                        <MdOutlineKeyboardArrowDown style={{ color: SMS_COLOR }} />
                      </div>
                    </Dropdown>
                    <span className="text-[11px] font-medium text-gray-400">to</span>
                    <Dropdown
                      trigger={["click"]}
                      menu={{
                        items: personNumbers,
                        selectedKeys: selectedSenderPhone ? [selectedSenderPhone] : undefined,
                        onClick: ({ key }) => setSelecetdSenderPhone(key),
                      }}
                    >
                      <div
                        className="flex cursor-pointer items-center gap-1.5 rounded-full px-2.5 py-0.5"
                        style={{
                          border: `1px solid ${SMS_COLOR}33`,
                          background: `${SMS_COLOR}08`,
                        }}
                      >
                        <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-green-500" />
                        <span className="text-[11.5px] font-medium" style={{ color: SMS_COLOR }}>
                          {selectedSenderPhone ?? "-"}
                        </span>
                        <MdOutlineKeyboardArrowDown style={{ color: SMS_COLOR }} />
                      </div>
                    </Dropdown>
                  </div>
                )}
              </div>

              {/* Subject — only for email */}
              {channels.email && (
                <div
                  className="mb-1.5 flex items-center gap-2"
                  style={{
                    borderBottom: subjectError ? "1px solid #ff4d4f" : "1px solid #f0f0f8",
                    paddingBottom: 4,
                  }}
                >
                  <span
                    style={{
                      fontSize: 11.5,
                      fontWeight: 600,
                      color: subjectError ? "#ff4d4f" : "#9ca3af",
                      flexShrink: 0,
                      userSelect: "none",
                      letterSpacing: "0.01em",
                    }}
                  >
                    Subject
                  </span>
                  <div
                    style={{
                      width: 1,
                      height: 12,
                      background: subjectError ? "#ff4d4f44" : "#e5e7eb",
                      flexShrink: 0,
                    }}
                  />
                  <Input
                    variant="borderless"
                    placeholder="Email subject line"
                    value={subject}
                    onChange={(e) => {
                      setSubject(e.target.value);
                      if (subjectError && e.target.value.trim()) setSubjectError(false);
                    }}
                    status={subjectError ? "error" : undefined}
                    className="text-[12.5px] text-gray-700"
                    style={{
                      padding: 0,
                      flex: 1,
                      borderRadius: 0,
                      boxShadow: "none",
                    }}
                    autoComplete="new-password"
                    name="email-subject-no-autofill"
                  />
                </div>
              )}

              {/* Editor */}
              <div
                onFocus={() => setFocused(true)}
                onBlur={() => setFocused(false)}
                className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-lg transition-colors duration-150"
                style={{
                  border: focused ? `1.5px solid ${EMAIL_COLOR}` : "1.5px solid #e5e7eb",
                }}
              >
                <div className="min-h-0 flex-1">
                  <RichTextEditor
                    value={replyText}
                    onChange={(val) => {
                      setReplyText(val);
                      setReplyError("");
                    }}
                  />
                </div>

                {/* Editor footer */}
                <div className="flex items-center justify-between border-t border-gray-100 bg-gray-50 px-2.5 py-1.5">
                  <div className="flex items-center gap-1.5">
                    <input ref={fileInputRef} type="file" multiple className="hidden" onChange={handleFileChange} />
                    <Popover
                      open={attachedFiles.length > 0 && attachmentPopoverOpen}
                      onOpenChange={setAttachmentPopoverOpen}
                      trigger="click"
                      placement="topLeft"
                      title={<span className="text-[12px] font-semibold">Attachments ({attachedFiles.length})</span>}
                      content={
                        <div className="w-[280px]">
                          <div className="mb-2 max-h-[200px] overflow-y-auto">
                            {attachedFiles.map((file, index) => {
                              const type = getContentTypeFromFile(file);
                              return (
                                <div
                                  key={index}
                                  className="mb-1.5 flex items-center justify-between rounded-md border border-gray-100 bg-gray-50 p-2"
                                >
                                  <div className="flex min-w-0 flex-1 items-center gap-2">
                                    <div
                                      className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-md ${getClassName(type)}`}
                                    >
                                      {getIcons(type)}
                                    </div>
                                    <div className="min-w-0 flex-1">
                                      <div className="overflow-hidden text-ellipsis whitespace-nowrap text-[12px] font-medium text-gray-700">
                                        {file.name}
                                      </div>
                                      <div className="text-[11px] text-gray-400">{formatBytes(file.size)}</div>
                                    </div>
                                  </div>
                                  <Button
                                    type="text"
                                    danger
                                    size="small"
                                    icon={<TbX size={13} />}
                                    onClick={() => handleRemoveFile(index)}
                                  />
                                </div>
                              );
                            })}
                          </div>
                          <Button
                            block
                            size="small"
                            icon={<TbPlus size={13} />}
                            onClick={() => fileInputRef.current?.click()}
                          >
                            Add more
                          </Button>
                        </div>
                      }
                    >
                      <Badge color="purple" count={attachedFiles.length} size="small" offset={[-4, 4]}>
                        <Button
                          size="small"
                          icon={<TbPaperclip size={14} />}
                          onClick={() => {
                            if (attachedFiles.length === 0) fileInputRef.current?.click();
                          }}
                        />
                      </Badge>
                    </Popover>

                    {/* SMS char counter */}
                    {channels.sms && !channels.email && (
                      <span
                        className="text-[11px]"
                        style={{ color: replyText.length > SMS_REPLY_TEXT_LENGTH ? "#ef4444" : "#9ca3af" }}
                      >
                        {replyText.length}/{SMS_REPLY_TEXT_LENGTH}
                      </span>
                    )}
                  </div>

                  <button
                    onClick={handleSend}
                    className="flex items-center gap-1.5 rounded-md px-4 py-1.5 text-[13px] font-medium text-white transition-colors duration-150"
                    style={{
                      background: EMAIL_COLOR,
                      border: "none",
                      cursor: "pointer",
                    }}
                  >
                    Send
                    {sendReplyMutation.isPending || broadcastMessage.isPending ? (
                      <TbLoader2 size={13} style={{ animation: "spin 1s linear infinite" }} />
                    ) : (
                      <TbSend size={13} />
                    )}
                  </button>
                </div>
              </div>

              {/* Validation error */}
              {replyError && <span className="mt-1 block pl-0.5 text-[11px] text-red-500">{replyError}</span>}
            </div>
          </div>
        </div>
      </div>
      <EditPersonContactsModal open={addChannelsModal} onClose={() => setAddCahnnels(false)} person={personDetails} />
    </div>
  );
};

export default MessageView;
